/**
 * Broadcast on `window` whenever the effective location changes.
 *
 * Declared here rather than alongside the singleton so a consumer can listen without
 * importing the implementation: components outside the `js/core` bundle that import
 * `location-singleton` would ship a second copy of the class and instantiate a rival
 * instance.  This module has no side effects, so importing the name costs nothing.
 */
export const LOCATION_CHANGE_EVENT = 'gmx-location-change'

/** Fires once the singleton has registered itself as `window.gmxLocationSingleton`. */
export const LOCATION_READY_EVENT = 'gmxLocationSingletonReady'

/**
 * Precedence tiers for a stored location, lowest to highest confidence:
 * an Akamai geo-IP guess, the signed-in user's profile, or an explicit entry.
 */
export type GMXLocationSource = 'user_input' | 'profile' | 'akamai'

export interface GMXLocationValue {
	zip: string,
	source: GMXLocationSource,
	/** ISO-8601 UTC datetime of when the value was last written. */
	timestamp: string,
	country?: string,
	region?: string,
	city?: string,
	county?: string,
}

/** Optional geographic detail that can accompany a zip on write. */
export type GMXLocationDetail = Pick<GMXLocationValue, 'country' | 'region' | 'city' | 'county'>

export interface GMXLocationChangeEventDetail {
	location: GMXLocationValue | null,
	previous: GMXLocationValue | null,
}

/**
 * The singleton as consumers see it on `window`.  Declaring the shape here lets a component
 * outside the `js/core` bundle stay typed without importing the implementation.
 */
export interface GMXLocationSingletonApi {
	get(): GMXLocationValue | null,
	getZip(): string | null,
	getAkamaiLocationCookieInfo(): AkamaiLocationCookieModel | null,
	setUserZip(zip: string, detail?: GMXLocationDetail): GMXLocationValue,
	set(zip: string, source: GMXLocationSource, detail?: GMXLocationDetail): GMXLocationValue,
	clear(): void,
	resolve(forceRefresh?: boolean): Promise<GMXLocationValue | null>,
}

/** Types `window.addEventListener('gmx-location-change', …)` for consuming apps. */
declare global {
	interface WindowEventMap {
		'gmx-location-change': CustomEvent<GMXLocationChangeEventDetail>,
	}
}

/**
 * Address record from `gbpe.user.profile`.  Only the fields the location
 * singleton reads are modelled; the payload carries more.
 */
export interface GBPEProfileAddress {
	postal_code?: string,
	preferred_address_flag?: boolean,
}

/** Parsed shape of the `GMWP_location` cookie set at the CDN edge. */
export interface AkamaiLocationCookieModel {
	country_code?: string,
	region_code?: string,
	city?: string,
	county?: string,
	zip?: string,
}
